import React, { useCallback, useEffect, useMemo } from "react";

import clsx from "clsx";
import { chunk, flatten, isEmpty, keyBy } from "lodash";
import AutoSizer from "react-virtualized-auto-sizer";
import { useElementSize } from "usehooks-ts";

import { Creative } from "@components/creatives";
import { Loading, LoadingSlideNoText } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import {
  FETCH_CREATIVES,
  FETCH_CREATIVE_PREVIEWS,
  FETCH_METRIC_PERCENTILES,
} from "@gql/creative-analytics";
import { useNorthbeamQuery } from "@hooks/http";
import {
  CreativePlatformEnum,
  type FetchCreativePreviews,
  type FetchCreativePreviewsVariables,
  type FetchCreatives,
  type FetchCreativesVariables,
  type FetchCreatives_me_creatives_rows,
  type FetchMetricPercentiles,
  type FetchMetricPercentilesVariables,
  type FetchMetricPercentiles_me_metricPercentiles_metrics,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { emptyText } from "@north-beam/nb-common";
import { Badge } from "@shared/badge";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import {
  type AdColouring,
  allAdColours,
  useAvailableAdColoursAtom,
  useAvailableMetricsByIdAtom,
  useCurrentNumberOfAdsAtom,
  usePreviewAdAtom,
} from "../atoms";
import {
  type ReportBodyState,
  type SelectedAdsByAdKey,
  useReportBodyState,
} from "../hooks/ca-control-state";
import { getCreativeType } from "../utils/platform-metrics";

import {
  CreativeAnalyticsCard,
  getIconForPlatform,
  statusColourMapping,
} from "./ca-card";

export type MetricPercentileByMetricId = Record<
  string,
  {
    readonly id: string;
    readonly platform: CreativePlatformEnum;
    readonly percentiles: FetchMetricPercentiles_me_metricPercentiles_metrics;
  }
>;

const allPlatforms = [
  CreativePlatformEnum.facebook,
  CreativePlatformEnum.tiktok,
  CreativePlatformEnum.snapchat,
  CreativePlatformEnum.pinterest,
  CreativePlatformEnum.adwords,
  CreativePlatformEnum.youtube,
  CreativePlatformEnum.applovin,
];

export function CreativeAnalyticsCardSleeve() {
  const { addQueryMetadata } = useQueryMetadata();
  const {
    attributionModel,
    attributionWindow,
    dateRange,
    selectedPlatforms,
    groupAdsByName,
    sorting,
    sortingOrder,
    searchText,
    activeStatus,
    heroMetricId,
    metrics: selectedMetrics,
    creativeType,
    selectedAdsByAdKey,
    setSelectedAdsByAdKey,
    metricFilters,
    breakdownFilters,
  } = useReportBodyState();

  const [availableMetricsById] = useAvailableMetricsByIdAtom();
  const [availableAdColours, setAvailableColours] = useAvailableAdColoursAtom();
  const [, setAdCount] = useCurrentNumberOfAdsAtom();
  const [ad] = usePreviewAdAtom();
  const [sizeRef, { width }] = useElementSize();
  const batches = useMemo(() => {
    if (width < 1500) {
      return 4;
    }
    if (width < 2000) {
      return 5;
    }
    if (width < 2200) {
      return 6;
    }
    if (width < 2500) {
      return 7;
    }
    return 8;
  }, [width]);

  const { user } = useUser();
  const { data: metricsPercentileData, loading: metricsPercentileLoading } =
    useNorthbeamQuery<FetchMetricPercentiles, FetchMetricPercentilesVariables>(
      FETCH_METRIC_PERCENTILES,
      {
        variables: {
          dateRange,
          attributionMethod: attributionModel,
          attributionWindow,
          granularity: "ad",
          nbAccountingMode: "accrual",
          timeGranularity: "daily",
          platforms: selectedPlatforms,
          groupByName: groupAdsByName,
          metrics: Object.values(availableMetricsById).map((metric) => ({
            metric: {
              id: metric.id,
              details: {
                type: "",
                ...metric.composition.details,
                accountingMode: "accrual",
                am: attributionModel,
                aw: attributionWindow,
              },
              metric: metric.composition.metric,
            },
            customComposed: metric.composition.customComposed,
            type: metric.composition.type,
          })),
        },
        onCompleted(data) {
          addQueryMetadata({
            context: "Creative Analytics",
            topic: "Metric Percentiles",
            metadata: data.me.metricPercentiles.queryMetadata,
          });
        },
      },
    );

  const metricPercentileByMetricIdAndPlatform = useMemo(() => {
    const metricsPercentilesById = keyBy(
      metricsPercentileData?.me.metricPercentiles.metrics,
      (item) => `${item.platform}-${item.metricId}`,
    );

    const percentileMetrics = Object.values(availableMetricsById).map(
      (metric) => {
        return allPlatforms.map(
          (platform) =>
            ({
              id: metric.id,
              platform,
              percentiles: metricsPercentilesById?.[`${platform}-${metric.id}`],
            } as const),
        );
      },
    );

    return keyBy(
      flatten(percentileMetrics),
      (item) => `${item.platform}-${item.id}`,
    );
  }, [
    availableMetricsById,
    metricsPercentileData?.me.metricPercentiles.metrics,
  ]);

  const fetchServerPage = useCallback(
    async (limit: number, nextPage = 0) => {
      const result = await user.apolloClient.query<
        FetchCreatives,
        FetchCreativesVariables
      >({
        query: FETCH_CREATIVES,
        variables: {
          dateRange,
          attributionMethod: attributionModel,
          attributionWindow,
          granularity: "ad",
          nbAccountingMode: "accrual",
          timeGranularity: "daily",
          offset: nextPage * limit * batches,
          limit: limit * batches,
          platforms: selectedPlatforms,
          metricFilters: isEmpty(metricFilters) ? undefined : metricFilters,
          breakdownFilters: breakdownFilters.flatMap((e) =>
            e.values.flatMap((value) => ({ breakdown: e.key, value })),
          ),
          sorting: {
            customComposed: sorting.customComposed,
            type: sorting.type,
            metric: {
              details: {
                accountingMode: "accrual",
                am: attributionModel,
                aw: attributionWindow,
                type: "",
                ...sorting.metric?.details,
              },
              ...sorting.metric,
            },
            order: sortingOrder,
          },
          status: activeStatus,
          searchText: searchText?.trim() ? searchText?.trim() : null,
          groupByName: groupAdsByName,
          creativeType,
        },
      });

      addQueryMetadata({
        context: "Creative Analytics",
        topic: "Metric Tiles",
        metadata: result.data.me.creatives.queryMetadata,
      });

      const rows = chunk(result.data.me.creatives.rows, batches);

      return { rows, hasMore: result.data.me.creatives.hasMore };
    },

    [
      user.apolloClient,
      dateRange,
      attributionModel,
      attributionWindow,
      batches,
      selectedPlatforms,
      metricFilters,
      breakdownFilters,
      sorting.customComposed,
      sorting.type,
      sorting.metric,
      sortingOrder,
      activeStatus,
      searchText,
      groupAdsByName,
      creativeType,
    ],
  );

  const {
    status,
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "creative-analytics",
      attributionModel,
      attributionWindow,
      dateRange.startDate,
      dateRange.endDate,
      selectedPlatforms,
      groupAdsByName,
      sorting,
      sortingOrder,
      activeStatus,
      searchText,
      creativeType,
      metricFilters,
      breakdownFilters,
      batches,
    ],
    queryFn: ({ pageParam }) => fetchServerPage(10, pageParam),
    getNextPageParam: (_lastGroup, groups) =>
      _lastGroup.hasMore ? groups.length : undefined,
    initialPageParam: 0,
  });

  const allRows = useMemo(
    () => (data ? data.pages.flatMap((d) => d.rows) : []),
    [data],
  );

  useEffect(() => {
    if (
      allRows.length &&
      data?.pages.length === 1 &&
      isEmpty(selectedAdsByAdKey)
    ) {
      const firstFourRows = allRows[0].slice(0, 4);

      const newSelectedAdsByAdKey: SelectedAdsByAdKey = {};
      firstFourRows.forEach((row, index) => {
        newSelectedAdsByAdKey[row.adKey] = {
          adKey: row.adKey,
          adColouring: allAdColours[index],
          adVisualId: `Ad ${index + 1}`,
        };
      });
      setSelectedAdsByAdKey(newSelectedAdsByAdKey);
      setAvailableColours(
        allAdColours.slice(firstFourRows.length, allAdColours.length),
      );
      setAdCount(firstFourRows.length + 1);
    }
    // do not want it to update every time you update the selectedAdsByAdKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, setAdCount, setAvailableColours, setSelectedAdsByAdKey]);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const numberOfMetricRows = Math.ceil(selectedMetrics.size / 2);
  const cardHeight =
    504 + 44 * numberOfMetricRows + 8 * Math.max(0, numberOfMetricRows - 1);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => cardHeight,
    overscan: 2,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [cardHeight, rowVirtualizer]);

  React.useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= allRows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNextPage,
    fetchNextPage,
    allRows.length,
    isFetchingNextPage,

    // Do not remove, we specifically use this
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <div
      className={clsx("flex flex-col pt-2 px-5 relative grow mb-2")}
      ref={sizeRef}
    >
      <PreviewModal ad={ad} />
      <div className="h-full">
        <AutoSizer disableWidth>
          {({ height }) => {
            return status === "pending" ? (
              <Loading />
            ) : status === "error" ? (
              <div
                className={clsx(
                  "w-50 mx-auto flex flex-col items-center",
                  "justify-center relative text-lg gap-4 py-2",
                )}
              >
                <h4 className="text-center font-bold mb-0">
                  We&apos;ve run into an issue processing your request
                </h4>
                <p className="text-center text-xl mb-0">What to do: </p>
                <ol className="gap-2">
                  <li>
                    Click <b>refresh</b> below.
                  </li>
                  <li>
                    If that does not work, try again in <b>5 minutes.</b>
                  </li>
                  <li>
                    If the page is still not working, please contact us at your
                    convenience. We will be in touch ASAP.
                  </li>
                </ol>
                <div className="flex justify-center">
                  <PrimaryButton
                    tabIndex={40}
                    onClick={() => window.location.reload()}
                  >
                    REFRESH
                  </PrimaryButton>
                </div>
              </div>
            ) : (
              <div
                ref={parentRef}
                style={{
                  height,
                  width: "100%",
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    /* either we get the total size, or we get a big enough size show the
                       "No items" component center-aligned */
                    height: `${
                      rowVirtualizer.getTotalSize() || height - 100
                    }px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {!rowVirtualizer.getVirtualItems().length && (
                    <div
                      className={clsx(
                        "w-full h-full text-center  text-gray-0",
                        "text-2xl font-light flex justify-center items-center",
                      )}
                    >
                      <div>No results matching your selections</div>
                    </div>
                  )}
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > allRows.length - 1;
                    const adRows = allRows[virtualRow.index];

                    return (
                      <div
                        key={virtualRow.key}
                        className={clsx("flex flex-col items-center")}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {isLoaderRow ? (
                          hasNextPage ? (
                            <Loading />
                          ) : null
                        ) : (
                          <CreativeAnalyticsGroup
                            rows={adRows}
                            availableMetricsById={availableMetricsById}
                            availableAdColours={availableAdColours}
                            heroMetricId={heroMetricId}
                            attributionModel={attributionModel}
                            attributionWindow={attributionWindow}
                            selectedMetrics={selectedMetrics}
                            metricPercentileByMetricIdAndPlatform={
                              metricPercentileByMetricIdAndPlatform
                            }
                            metricPercentilesLoading={metricsPercentileLoading}
                            columnCount={batches}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
}

function CreativeAnalyticsGroup({
  rows,
  availableMetricsById,
  availableAdColours,
  heroMetricId,
  attributionModel,
  attributionWindow,
  selectedMetrics,
  metricPercentileByMetricIdAndPlatform,
  metricPercentilesLoading,
  columnCount,
}: {
  rows: FetchCreatives_me_creatives_rows[];
  availableMetricsById: Record<string, SalesPageMetric>;
  availableAdColours: AdColouring[];
  heroMetricId: string;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
  selectedMetrics: ReportBodyState["metrics"];
  metricPercentileByMetricIdAndPlatform: MetricPercentileByMetricId;
  metricPercentilesLoading: boolean;
  columnCount: number;
}) {
  // we need this because i am porting backwards compatibilit
  // TODO: at some point resolve this por
  const metricsBySalesTableMetridId = useMemo(() => {
    const allMetrics = Object.values(availableMetricsById);
    const allSalesTableIdMetrics = allMetrics.filter(
      (spec) => spec.metadata.salesTableMetricId,
    );
    return keyBy(
      allSalesTableIdMetrics,
      (spec) => spec.metadata.salesTableMetricId,
    ) as Record<string, SalesPageMetric>;
  }, [availableMetricsById]);
  return (
    <div
      className={clsx("grid gap-5 relative", {
        "grid-cols-4": columnCount === 4,
        "grid-cols-5": columnCount === 5,
        "grid-cols-6": columnCount === 6,
        "grid-cols-7": columnCount === 7,
        "grid-cols-8": columnCount === 8,
      })}
    >
      {rows.map((row) => (
        <CreativeAnalyticsCard
          key={row.adKey}
          ad={row}
          availableMetricsById={availableMetricsById}
          availableAdColours={availableAdColours}
          heroMetricId={heroMetricId}
          attributionModel={attributionModel}
          attributionWindow={attributionWindow}
          selectedMetrics={selectedMetrics}
          metricPercentileByMetricIdAndPlatform={
            metricPercentileByMetricIdAndPlatform
          }
          metricPercentilesLoading={metricPercentilesLoading}
          metricsBySalesTableMetridId={metricsBySalesTableMetridId}
        />
      ))}
    </div>
  );
}

function PreviewModal({ ad }: { ad: FetchCreatives_me_creatives_rows | null }) {
  const [, setAd] = usePreviewAdAtom();
  const onClose = useCallback(() => setAd(null), [setAd]);

  return (
    <Modal
      isOpen={!!ad}
      onClose={onClose}
      titleContent={
        <div className="text-2xl font-semibold text-v2-primary">Ad preview</div>
      }
      size="md"
      contentClassName="h-[calc(100%-50px)] bg-v2-primary"
    >
      {ad && <PreviewModalContent ad={ad} />}
    </Modal>
  );
}

function PreviewModalContent({ ad }: { ad: FetchCreatives_me_creatives_rows }) {
  const { groupAdsByName } = useReportBodyState();

  const { data, loading } = useNorthbeamQuery<
    FetchCreativePreviews,
    FetchCreativePreviewsVariables
  >(FETCH_CREATIVE_PREVIEWS, { variables: { adKey: ad.previewAdKey } });
  const adName = ad.adName ?? emptyText;
  const adset = ad.adsetName ?? emptyText;
  const campaign = ad.campaignName ?? emptyText;
  const platform = ad.dimensions["Platform (Northbeam)"];

  const Icon = getIconForPlatform(ad);

  return (
    <div>
      <div className="flex flex-col space-y-3 mt-2 text-sm">
        <div className="flex flex-row">
          <div className="text-gray mr-2 min-w-[4.5rem] max-w-[4.5rem]">Ad</div>
          <div className="truncate">
            {groupAdsByName ? (
              <div className="truncate font-normal">{adName}</div>
            ) : (
              <Badge
                textClassname="ml-1 font-normal"
                text={ad.name ?? "N/A"}
                badgeColor={statusColourMapping(ad.status)}
                size="md"
              />
            )}
          </div>
        </div>
        <div className="flex flex-row">
          <div className="text-gray mr-2 min-w-[4.5rem] max-w-[4.5rem]">
            Adset
          </div>
          <div className="truncate">{adset}</div>
        </div>
        <div className="flex flex-row">
          <div className="text-gray mr-2 min-w-[4.5rem] max-w-[4.5rem]">
            Campaign
          </div>
          <div className="truncate">{campaign}</div>
        </div>
        <div className="flex flex-row">
          <div className="text-gray mr-2 min-w-[4.5rem] max-w-[4.5rem]">
            Platform
          </div>
          <div className="flex flex-row items-center">
            <Icon className="mr-1.5" />
            {platform}
          </div>
        </div>
      </div>
      <div className="flex items-center mt-7 flex-col h-full">
        {loading && (
          <div className="h-96 w-full flex items-center justify-center">
            <LoadingSlideNoText />
          </div>
        )}
        {!loading && !data?.me.creativePreviews.previews?.length && (
          <PlatformTooltip platform={getCreativeType(ad)} />
        )}
        {getCreativeType(ad) !== CreativePlatformEnum.applovin ? (
          data?.me.creativePreviews.previews.map((preview, index) => (
            <Creative key={index} ad={preview} />
          ))
        ) : (
          <div>Not available</div>
        )}
      </div>
    </div>
  );
}

function PlatformTooltip({ platform }: { platform?: CreativePlatformEnum }) {
  if (platform === CreativePlatformEnum.tiktok) {
    return (
      <div className="flex text-gray text-lg mt-5">
        <GenericTooltip
          content={
            "If this is a Spark Ad, it is likely that preview creation " +
            "is unavailable because your account no longer has access to create previews " +
            "for the corresponding TikTok post"
          }
        >
          Preview not available
        </GenericTooltip>
      </div>
    );
  }
  return (
    <div className="flex text-gray text-lg mt-5">Preview not available</div>
  );
}
